import type { EditorState, Transaction } from 'prosemirror-state'
import type SpaceVariable from '@/modules/space/models/SpaceVariable'
import type User from '@/modules/user/models/User'
import { buildAssignCalloutAttributes, getAvatarColorIndex, getAvatarInitial } from '@/util/variable-callout-utils'
import { getVariableIconState, getVariableValueFlags } from '@/util/variable-state'

/**
 * The transaction that shows a variable's current assignment on its placeholders: mark attributes (default,
 * recipient, icon, avatar) and the assign callout. Null when nothing changes. A live block dispatches it; a static
 * block applies it to its stored HTML (static-variable-transforms.ts). Moved unchanged from MessageElementComponent.
 */
export function buildVariableAssignmentTransaction(
  state: EditorState,
  variable: SpaceVariable,
  variableId: string,
  variableName: string,
  users: User[]
): Transaction | null {
  const { hasDefault, hasRecipient } = getVariableValueFlags(variable)
  const recipientEmail = variable.assignedToRecipientName?.trim() || undefined
  const iconState = getVariableIconState(variable)
  const avatarLetter = iconState === 'pend' && recipientEmail ? getAvatarInitial(undefined, recipientEmail) : null
  const avatarColorIndex =
    iconState === 'pend' && recipientEmail ? getAvatarColorIndex(recipientEmail).toString() : null

  // Build mark attributes once
  const newMarkAttributes: Record<string, string | null> = {
    'data-has-default': hasDefault ? 'true' : 'false',
    'data-has-recipient': hasRecipient ? 'true' : null,
    'data-icon-state': iconState,
    'data-avatar-letter': avatarLetter,
    'data-avatar-color-index': avatarColorIndex
  }

  if (variableId) {
    newMarkAttributes['data-variable-id'] = variableId
  }
  if (variableName) {
    newMarkAttributes['data-variable-name'] = variableName
  }

  // Build callout attributes (only if recipient exists and no default)
  const { doc, schema } = state
  const variablePlaceholderMarkType = schema.marks.variablePlaceholder
  const variableWrapperNodeType = schema.nodes.variableWrapper
  const assignCalloutType = schema.nodes.assignCallout

  if (!variablePlaceholderMarkType || !variableWrapperNodeType) {
    return null
  }

  // Helper function to extract variable name from text content
  const extractVariableNameFromText = (text: string | null | undefined): string | null => {
    if (!text) return null
    const match = text.match(/\{\{\s*([^}]+?)\s*\}\}/)
    return match?.[1]?.trim() || null
  }

  // Normalize variable name for case-insensitive comparison
  const normalizedVariableName = variableName?.toLowerCase().trim()

  // Single traversal: collect mark updates and callout positions
  // Use a map to track ranges by wrapper position to avoid duplicates
  const markUpdatesByWrapper = new Map<number, { from: number; to: number }>()
  const calloutDeletions: Array<{ pos: number; size: number }> = []
  const calloutUpdates: Array<{ pos: number; node: any }> = []
  const matchedWrapperPositions = new Set<number>()

  // Find all variable wrapper nodes first, then check their content
  doc.descendants((node, pos) => {
    if (node.type !== variableWrapperNodeType) {
      return true
    }

    // Check if this wrapper contains the variable we're looking for
    let foundMatch = false
    let markStart = -1
    let markEnd = -1

    // Find all text nodes with matching mark within this wrapper
    const wrapperStart = pos + 1 // Start after wrapper opening
    let offset = wrapperStart

    node.forEach(childNode => {
      if (childNode.isText && childNode.marks) {
        const mark = childNode.marks.find(m => m.type === variablePlaceholderMarkType)
        if (mark) {
          const markVariableId = mark.attrs['data-variable-id']
          const markVariableName = mark.attrs['data-variable-name']

          // Match by variable ID (primary) OR variable name (fallback)
          let isMatch = false

          if (variableId && markVariableId === variableId) {
            isMatch = true
          } else if (normalizedVariableName) {
            if (markVariableName && markVariableName.toLowerCase().trim() === normalizedVariableName) {
              isMatch = true
            } else {
              const extractedName = extractVariableNameFromText(childNode.text)
              if (extractedName && extractedName.toLowerCase().trim() === normalizedVariableName) {
                isMatch = true
              }
            }
          }

          if (isMatch) {
            foundMatch = true
            // Track the start and end of the continuous range
            if (markStart === -1) {
              markStart = offset
            }
            markEnd = offset + childNode.nodeSize
          }
        }
      }
      offset += childNode.nodeSize
    })

    if (foundMatch && markStart !== -1 && markEnd !== -1) {
      // Store the range for this wrapper - this should cover all matching text nodes
      markUpdatesByWrapper.set(pos, { from: markStart, to: markEnd })
      matchedWrapperPositions.add(pos)
    }

    return true
  })

  // Convert map to array
  const markUpdates = Array.from(markUpdatesByWrapper.values())

  // Second pass: Find and update/delete/create callouts in matched wrappers
  const calloutCreations: Array<{ pos: number }> = []

  matchedWrapperPositions.forEach(wrapperPos => {
    let wrapperNode = doc.nodeAt(wrapperPos)

    // Verify we have the right node - if not, search for it
    if (wrapperNode?.type !== variableWrapperNodeType) {
      // Re-search for the wrapper
      doc.descendants((node, pos) => {
        if (node.type === variableWrapperNodeType) {
          // Check if any of our mark updates are inside this wrapper
          const wrapperEnd = pos + node.nodeSize
          const hasMatchingMark = markUpdates.some(m => m.from >= pos && m.from < wrapperEnd)
          if (hasMatchingMark) {
            matchedWrapperPositions.delete(wrapperPos)
            matchedWrapperPositions.add(pos)
            return false // Stop searching
          }
        }
        return true
      })
      // Get the updated wrapper node
      const updatedPos = Array.from(matchedWrapperPositions).find(p => doc.nodeAt(p)?.type === variableWrapperNodeType)
      if (updatedPos !== undefined) {
        wrapperPos = updatedPos
        wrapperNode = doc.nodeAt(updatedPos)
      }
    }

    if (wrapperNode?.type === variableWrapperNodeType && assignCalloutType) {
      let calloutOffset = 1
      let hasCallout = false

      wrapperNode.forEach(child => {
        const isAssignCallout = child.type === assignCalloutType || child.type.name === 'assignCallout'

        if (isAssignCallout) {
          hasCallout = true
          // Update callout attributes (keep callout for all variables - shown on hover)
          calloutUpdates.push({
            pos: wrapperPos + calloutOffset,
            node: child
          })
        }
        calloutOffset += child.nodeSize
      })

      // Create callout if it doesn't exist (shown on hover for all variables)
      if (!hasCallout) {
        // Insert callout after the variable text node (at the end of wrapper content)
        calloutCreations.push({
          pos: wrapperPos + wrapperNode.content.size
        })
      }
    }
  })

  // Apply all updates in a single transaction
  if (
    markUpdates.length > 0 ||
    calloutDeletions.length > 0 ||
    calloutUpdates.length > 0 ||
    calloutCreations.length > 0
  ) {
    let tr = state.tr

    // Process wrappers in reverse order to maintain positions
    const wrapperPositions = Array.from(matchedWrapperPositions).sort((a, b) => b - a)

    for (const originalWrapperPos of wrapperPositions) {
      // Use current transaction document state (positions may have shifted from previous operations)
      let currentWrapperPos = originalWrapperPos
      let wrapperNode = tr.doc.nodeAt(currentWrapperPos)

      // If wrapper not found, try to find it by searching (positions may have shifted)
      if (!wrapperNode || wrapperNode.type !== variableWrapperNodeType) {
        // Search for the wrapper node that contains our variable
        tr.doc.descendants((node, pos) => {
          if (node.type === variableWrapperNodeType) {
            // Check if this wrapper contains our variable
            let foundVariable = false
            node.forEach(childNode => {
              if (childNode.isText && childNode.marks) {
                const mark = childNode.marks.find(m => m.type === variablePlaceholderMarkType)
                if (mark) {
                  const markVariableId = mark.attrs['data-variable-id']
                  const markVariableName = mark.attrs['data-variable-name']
                  if (
                    (variableId && markVariableId === variableId) ||
                    (normalizedVariableName &&
                      markVariableName &&
                      markVariableName.toLowerCase().trim() === normalizedVariableName)
                  ) {
                    foundVariable = true
                  }
                }
              }
            })
            if (foundVariable) {
              currentWrapperPos = pos
              wrapperNode = node
              return false // Stop searching
            }
          }
          return true
        })
      }

      // If still not found, skip
      if (!wrapperNode || wrapperNode.type !== variableWrapperNodeType) {
        continue
      }

      // Extract variable name from wrapper content
      let variableText = ''
      wrapperNode.forEach(childNode => {
        if (childNode.isText) {
          variableText += childNode.text
        }
      })

      // Extract variable name from text (format: {{ variableName }})
      const extractedName = extractVariableNameFromText(variableText)
      if (!extractedName || extractedName.toLowerCase().trim() !== normalizedVariableName) {
        continue
      }

      // Rebuild the variable text properly
      const properVariableText = `{{ ${variable.variableName} }}`

      // Calculate wrapper content range (excluding wrapper node itself)
      const wrapperContentStart = currentWrapperPos + 1
      const wrapperContentEnd = currentWrapperPos + wrapperNode.content.size + 1

      // Delete all wrapper content and replace with new content
      // First, collect what we need to insert
      const nodesToInsert: any[] = []

      // Create variable text node with proper mark
      const variableTextNode = schema.text(properVariableText, [variablePlaceholderMarkType.create(newMarkAttributes)])
      nodesToInsert.push(variableTextNode)

      // Add callout for all variables (shown on hover)
      if (assignCalloutType) {
        const calloutAttrs = buildAssignCalloutAttributes(variable, recipientEmail, users)
        const calloutNode = assignCalloutType.create(calloutAttrs)
        nodesToInsert.push(calloutNode)
      }

      // Replace wrapper content: delete old content and insert new nodes
      tr = tr.replaceWith(wrapperContentStart, wrapperContentEnd, nodesToInsert)
    }

    // Rewriting a wrapper with what it already holds still counts as a doc change and would queue another save
    return tr.doc.eq(state.doc) ? null : tr
  }
  return null
}
